// frontend/my-react-app/src/utils/timeUtils.js
// Created: 2026-03-10
// Time conversion helpers for MedicationForm — 12-hr slot objects ↔ 24-hr "HH:MM" strings, timezone label formatter.

/**
 * Converts a 24-hour "HH:MM" string to a 12-hour slot object.
 * "08:00" → { hour: 8, minute: "00", period: "AM" }
 * "20:30" → { hour: 8, minute: "30", period: "PM" }
 */
export function parse24hrToSlot(hhmm) {
  const [hourStr, minute] = hhmm.split(':');
  const hour24 = parseInt(hourStr, 10);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return { hour, minute, period };
}

/**
 * Converts a 12-hour slot object to a 24-hour "HH:MM" string.
 * { hour: 8, minute: "00", period: "AM" } → "08:00"
 * { hour: 8, minute: "30", period: "PM" } → "20:30"
 */
export function slotTo24hr({ hour, minute, period }) {
  let hour24 = parseInt(hour, 10);
  if (period === 'AM') {
    if (hour24 === 12) hour24 = 0;
  } else {
    if (hour24 !== 12) hour24 += 12;
  }
  return `${String(hour24).padStart(2, '0')}:${minute}`;
}

/**
 * Converts an IANA timezone string to a friendly label for display.
 * "America/Los_Angeles" → "Times shown in Pacific Time"
 * Falls back to the raw IANA string if Intl can't resolve it.
 */
export function formatTimezoneLabel(ianaTimezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'long',
      hour: 'numeric',
      timeZone: ianaTimezone,
    }).formatToParts(new Date());

    const longName = parts.find(p => p.type === 'timeZoneName')?.value || '';
    // Strip "Standard" / "Daylight" / "Summer" to get e.g. "Pacific Time"
    const friendly = longName
      .replace(' Standard Time', ' Time')
      .replace(' Daylight Time', ' Time')
      .replace(' Summer Time', ' Time');

    return `Times shown in ${friendly || ianaTimezone}`;
  } catch {
    return `Times shown in ${ianaTimezone}`;
  }
}
